// =============================================================================
// MeuDriver — ETA até o ponto de encontro
// -----------------------------------------------------------------------------
// Provider: Mapbox Directions API (o mesmo token já usado pelo geocoding).
// Fallback: distância de Haversine ÷ velocidade média urbana.
//
// O fallback nunca é silencioso — o retorno traz `approximate: true` para a UI
// poder dizer "aproximadamente" em vez de cravar um horário que não veio de
// roteamento real.
// =============================================================================

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN as string | undefined

/** Velocidade média urbana usada só no fallback (km/h). */
const FALLBACK_SPEED_KMH = 30

export interface EtaResult {
  /** Segundos até o destino. */
  durationSeconds: number
  /** Metros até o destino (rota real, ou linha reta no fallback). */
  distanceMeters: number
  /** `true` quando não veio de roteamento real — a UI deve sinalizar. */
  approximate: boolean
}

export interface Coords {
  lat: number
  lng: number
}

// ─── Haversine ───────────────────────────────────────────────────────────────

const EARTH_RADIUS_M = 6_371_000

/** Distância em linha reta entre dois pontos, em metros. */
export function haversineMeters(a: Coords, b: Coords): number {
  const toRad = (deg: number) => (deg * Math.PI) / 180
  const dLat = toRad(b.lat - a.lat)
  const dLng = toRad(b.lng - a.lng)
  const lat1 = toRad(a.lat)
  const lat2 = toRad(b.lat)
  const h =
    Math.sin(dLat / 2) ** 2 + Math.sin(dLng / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2)
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)))
}

function fallbackEta(from: Coords, to: Coords): EtaResult {
  const distanceMeters = haversineMeters(from, to)
  const speedMs = (FALLBACK_SPEED_KMH * 1000) / 3600
  return {
    distanceMeters,
    durationSeconds: distanceMeters / speedMs,
    approximate: true,
  }
}

// ─── Mapbox Directions ───────────────────────────────────────────────────────

interface DirectionsResponse {
  routes?: { duration: number; distance: number }[]
}

/**
 * ETA de carro entre dois pontos.
 *
 * Nunca lança: qualquer falha (sem token, rede fora, rota inexistente, resposta
 * malformada) degrada para o cálculo aproximado. Um ETA impreciso é melhor do
 * que uma tela de erro enquanto o aluno espera o carro.
 */
export async function getEta(from: Coords, to: Coords, signal?: AbortSignal): Promise<EtaResult> {
  if (!MAPBOX_TOKEN) return fallbackEta(from, to)

  try {
    const coords = `${from.lng},${from.lat};${to.lng},${to.lat}`
    const url =
      `https://api.mapbox.com/directions/v5/mapbox/driving/${coords}` +
      `?overview=false&access_token=${MAPBOX_TOKEN}`

    const res = await fetch(url, { signal })
    if (!res.ok) return fallbackEta(from, to)

    const data = (await res.json()) as DirectionsResponse
    const route = data.routes?.[0]
    if (!route || !Number.isFinite(route.duration)) return fallbackEta(from, to)

    return {
      durationSeconds: route.duration,
      distanceMeters: route.distance,
      approximate: false,
    }
  } catch {
    return fallbackEta(from, to)
  }
}

// ─── Formatação ──────────────────────────────────────────────────────────────

/** "chegando", "3 min", "1 h 05 min". */
export function formatEta(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '—'
  const totalMinutes = Math.round(seconds / 60)
  if (totalMinutes < 1) return 'chegando'
  if (totalMinutes < 60) return `${totalMinutes} min`
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  return `${hours} h ${String(minutes).padStart(2, '0')} min`
}

/** "320 m", "4,7 km". */
export function formatDistance(meters: number): string {
  if (!Number.isFinite(meters) || meters < 0) return '—'
  if (meters < 1000) return `${Math.round(meters)} m`
  return `${(meters / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} km`
}
